import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS, LabelM } from '@sorare/core/src/atoms/typography';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { getLeagueAvatar } from '@sorare/marketplace/src/lib/newSeason';

import { Line } from '#src/components/essence/CardPullExperience/CardPullInfos/ui.tsx';
import { WithCompetitionStatusDot } from '#src/components/essence/CardPullExperience/CompetitionAvailability/WithCompetitionStatusDot.tsx';
import { splitByPoolAvailability } from '#src/components/essence/CardPullExperience/CompetitionAvailability/splitByPoolAvailability.ts';

import type { AvailableCompetitions_competition } from './__generated__/index.graphql.ts';

const messages = defineMessages({
  available: {
    defaultMessage: 'Available',
  },
  unavailable: {
    defaultMessage: 'Unavailable',
  },
});

type Props = {
  competitions: AvailableCompetitions_competition[];
  availableCompetitions: { slug: string }[];
};

const AVATAR_SIZE = 24;

const Root = styled(Vertical)`
  width: 100%;
  @media ${laptopAndAbove} {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const StyledLine = styled(Line)`
  width: 100%;
  @media ${laptopAndAbove} {
    width: calc(50% - var(--half-unit));
  }
`;

type SectionProps = {
  title: ReactNode;
  competitions: AvailableCompetitions_competition[];
  available: boolean;
};

const Section = ({ title, competitions, available }: SectionProps) => {
  if (competitions.length === 0) return null;

  return (
    <Vertical gap={1}>
      <BrandXS as="h3" color="var(--c-nd-600)">
        {title}
      </BrandXS>
      <Root>
        {competitions.map(competition => (
          <StyledLine key={competition.slug}>
            <WithCompetitionStatusDot
              available={available}
              ringColor="var(--c-nd-50-opaque)"
            >
              <LeagueAvatar
                league={getLeagueAvatar({ competition }, 'icon')}
                size={AVATAR_SIZE}
              />
            </WithCompetitionStatusDot>
            <LabelM>{competition.displayName}</LabelM>
          </StyledLine>
        ))}
      </Root>
    </Vertical>
  );
};

export const AvailableCompetitions = ({
  competitions,
  availableCompetitions,
}: Props) => {
  if (competitions.length === 0) return null;

  const { inPool, outOfPool } = splitByPoolAvailability(
    competitions,
    availableCompetitions
  );

  return (
    <Vertical gap={3}>
      <Section
        title={<FormattedMessage {...messages.available} />}
        competitions={inPool}
        available
      />
      <Section
        title={<FormattedMessage {...messages.unavailable} />}
        competitions={outOfPool}
        available={false}
      />
    </Vertical>
  );
};

AvailableCompetitions.fragments = {
  competition: gql`
    fragment AvailableCompetitions_competition on Competition {
      slug
      displayName
      ...getLeagueAvatar_competition
    }
    ${getLeagueAvatar.fragments.competition}
  ` as TypedDocumentNode<AvailableCompetitions_competition>,
};
